/**
 * Maps approval / notification entity_type values to SPA deep-link paths.
 * Keep in sync with backend/src/modules/notifications/entity-action-url-registry.ts
 * and App.tsx routes. Prefer /growth over legacy /sales (no prefix-redirect hop).
 */
import { epcCaDocumentBridgePath } from '@/pages/epc/commercial/epcCaDeepLinks';

type PathBuilder = (entityId: string) => string | null;

const REGISTRY: Record<string, PathBuilder> = {
  purchase_order: (id) => `/scm/procurement/purchase-orders/${id}`,
  supplier_advance_request: (id) => `/scm/procurement/supplier-advance-requests/${id}`,
  inland_process_deviation: (id) => `/growth/inland-process-deviations/${id}`,
  purchase_requisition: (id) => `/scm/procurement/purchase-requests/${id}`,
  goods_receipt_note: (id) => `/scm/procurement/grn/${id}`,
  purchase_return: (id) => `/scm/procurement/purchase-returns/${id}`,
  supplier_invoice: (id) => `/scm/procurement/supplier-invoices/${id}`,
  payment_voucher: (id) => `/fin/payment-vouchers/${id}`,
  journal_voucher: () => `/fin/gl/journals`,
  recognition_contract: (id) => `/fin/prepaid/contracts/${encodeURIComponent(id)}`,
  recognition_revision: (id) => `/fin/prepaid/adjustments/${encodeURIComponent(id)}`,
  education_admission: (id) => (id ? `/education/admissions/${id}` : '/education/admissions'),
  education_enrollment: (id) => (id ? `/education/enrollments/${id}` : '/education/enrollments'),
  education_withdrawal: (id) => (id ? `/education/enrollments/${id}/withdrawal` : '/education/enrollments'),
  education_progression_batch: (id) => (id ? `/education/progression/${id}` : '/education/progression'),
  education_student_leave: (id) => (id ? `/education/attendance/leave?leave=${id}` : '/education/attendance/leave'),
  education_attendance_amendment: (id) => (id ? `/education/attendance/corrections?amendment=${id}` : '/education/attendance/corrections'),
  recognition_run: (id) => `/fin/prepaid/runs/${encodeURIComponent(id)}`,
  sales_order: (id) => `/growth/orders/${id}`,
  sales_invoice: (id) => `/growth/invoices/${id}`,
  material_request: (id) =>
    id ? `/scm/inventory/material-requests/${id}` : `/scm/inventory/material-requests`,
  /** Runtime / inbox use material_issuance; registry historically used material_issue. */
  material_issuance: (id) =>
    id ? `/scm/inventory/material-issues/${id}` : `/scm/inventory/material-issues`,
  material_issue: (id) =>
    id ? `/scm/inventory/material-issues/${id}` : `/scm/inventory/material-issues`,
  stock_transfer: (id) =>
    id ? `/scm/warehouse/stock-transfer/${id}` : `/scm/warehouse/stock-transfer`,
  stock_adjustment: (id) => `/scm/warehouse/stock-adjustment/${id}`,
  quotation: (id) => `/growth/quotations/${id}`,
  contract_review: (id) => `/growth/contract-reviews/${id}`,
  order_review_checklist: (id) => `/growth/order-review-checklists/${id}`,
  estimation: (id) => `/growth/estimations/edit/${id}`,
  project_timesheet: () => `/prj/timesheets`,
  work_order: (id) => `/mfg/work-orders/${id}`,
  production_request: (id) => `/mfg/production-requests/${id}`,
  management_of_change: (id) => `/grc/moc/${id}`,
  epc_permit: (id) => `/epc/execution?tab=permits&permitId=${encodeURIComponent(id)}`,
  epc_estimate: (id) => `/epc/commercial/estimate-revision/${encodeURIComponent(id)}`,
  project_budget_revision: (id) => `/prj/budget-revisions/${encodeURIComponent(id)}`,
  project_budget_exceedance: (id) => `/prj/budget-exceedances/${encodeURIComponent(id)}`,
  delivery_note: (id) => `/growth/delivery-notes/${id}`,
  document_collection_request: (id) => `/growth/document-collections/${id}`,
  customer_satisfaction_survey: (id) => `/growth/customer-satisfaction/${id}`,
  customer_property_receipt: (id) => `/cspm/receipts/${id}`,
  customer_property_requirement: (id) => `/cspm/requirements/${id}`,
  customer_property_return: (id) => `/cspm/returns/${id}`,
  customer_property_exception: (id) => `/cspm?tab=exceptions&exception=${id}`,
  customer_property_reconciliation: (id) => `/cspm/reconciliations/${id}`,
  ncr_record: (id) => `/qms/ncrs/${id}`,
  capa_record: (id) => `/qms/capas/${id}`,
  management_review_agenda: (id) => `/qms/management-reviews/${id}`,
  management_review_minutes: (id) => `/qms/management-reviews/${id}`,
  controlled_document: (id) => `/qms/document-management/${id}`,
  stage_inspection: (id) => `/qms/inspections/${id}`,
  unified_task: (id) => (id ? `/home/tasks/${id}` : `/home/tasks`),
  operational_action: (id) =>
    id
      ? `/owner-intelligence/operational-actions?id=${encodeURIComponent(id)}`
      : '/owner-intelligence/operational-actions',
  tmmde_equipment: (id) => (id ? `/qms/tmmde/${id}` : '/qms/tmmde'),
  tmmde_impact_assessment: (id) => (id ? `/qms/tmmde/impact/${id}` : '/qms/tmmde/impact'),
  competency_assessment: (id) => (id ? `/grc/competency/assessments/${id}` : '/grc/competency/assessments'),
  training_session: (id) => (id ? `/grc/competency/sessions/${id}` : '/grc/competency/sessions'),
  training_effectiveness: (id) =>
    id ? `/grc/competency/effectiveness/${id}` : '/grc/competency/effectiveness',
  competency_employee: (id) => (id ? `/grc/competency/employees/${id}` : '/grc/competency'),
  training_schedule: () => '/grc/competency/schedule',
  asset: (id) => `/asset/assets/${id}/edit`,
  asset_capitalization: (id) => `/asset/assets/${id}/edit`,
  asset_disposal: (id) => `/asset/assets/${id}/edit`,
  asset_transfer: (id) => `/asset/assets/${id}/edit`,
  asset_audit_cycle: (id) => (id ? `/asset/audit?cycle=${encodeURIComponent(id)}` : '/asset/audit'),
  asset_report_hub: () => '/asset/reports',
  hr_manpower_requisition: (id) => (id ? `/hr/requisitions/${id}` : '/hr/requisitions'),
  hr_offer: (id) => (id ? `/hr/offers/${id}` : '/hr/offers'),
  hr_job_posting: (id) => (id ? `/hr/jobs/${id}` : '/hr/jobs'),
  hr_onboarding_task: () => `/hr/onboarding/my-tasks`,
  hr_letter_request: (id) => (id ? `/hr/letter-requests/${id}` : '/hr/letter-requests'),
  hr_leave_application: () => `/hr/leaves`,
  hr_ot_request: () => `/hr/ot`,
  hr_profile_change_request: () => `/hr/profile-change-requests`,
};

/**
 * SPA path for opening an approved/pending document from Approval Inbox / deep links.
 * Returns null when the entity type has no registered route (caller shows plain text).
 */
export function getEntityDetailPath(entityType: string, entityId: string): string | null {
  const type = String(entityType || '').trim();
  const id = String(entityId || '').trim();
  if (!type) return null;

  const caPath = epcCaDocumentBridgePath(type, id);
  if (caPath) return caPath;

  const builder = REGISTRY[type];
  if (!builder) return null;
  const path = builder(id);
  if (!path) return null;
  return path.startsWith('/') ? path : `/${path}`;
}
